import library from "../data/exercises.json";
import type { ExerciseLibrary, SchemaField, Selection, Exercise } from "./types";

export const lib = library as unknown as ExerciseLibrary;

export function emptySelection(): Selection {
  return {
    classDate: "",
    selects: {},
    checkboxes: {},
  };
}

// ─── Schema layout helpers ───────────────────────────────────────────────
// The original form is one long flat list of fields, where `section` fields
// act as headings ("Thighs", "Seat", "Core Focus"). We group fields between
// sections so the form renders as `<section><heading><fields/></section>`.

export interface FormSection {
  title: string;        // "" for the implicit top section (date/email)
  fields: SchemaField[];
}

export function groupSchemaIntoSections(schema: SchemaField[]): FormSection[] {
  const sections: FormSection[] = [{ title: "", fields: [] }];
  for (const f of schema) {
    if (f.kind === "section") {
      // Even a section with empty title acts as a divider for the next group.
      sections.push({ title: f.title, fields: [] });
    } else if (f.kind !== "unknown") {
      sections[sections.length - 1].fields.push(f);
    }
  }
  // Drop empty groups (some Gravity-Forms section dividers have no body).
  return sections.filter((s) => s.fields.length > 0);
}

// ─── Resolving a selection to exercises ──────────────────────────────────
// The Preview view turns a Selection into a list of Exercise instances in
// the canonical class order.
//
// Canonical order (verified from output PDF):
//   1. Triceps                       (input 130 — checkbox)
//   2. Thighs 1, 2, 3                (inputs 154, 157, 159 — selects)
//      with Tube alternates          (inputs 156, 158, 160)
//   3. Thigh Stretch                 (input 135 — checkbox)
//   4. Seat 1, 2                     (inputs 161, 163 — selects)
//      with Tube alternates          (inputs 162, 164)
//   5. Core Focus                    (inputs 140, 141, 142, 143, 394, 395 — checkboxes)
//   6. Abs 1, 2, 3                   (inputs 7, 8, 9 — checkboxes)
//      with Tube alternates          (inputs 69, 70, 71)
//   7. Back Extension                (input 10 — checkbox)
//   8. Back Dancing                  (input 11 — checkbox)
//      with Tube alternates          (input 72)

const PRINT_ORDER: string[] = [
  "130",
  "154", "156", "157", "158", "159", "160",
  "135",
  "161", "162", "163", "164",
  "140", "141", "142", "143", "394", "395",
  "7", "69", "8", "70", "9", "71",
  "10",
  "11", "72",
];

export interface ResolvedExercise {
  exercise: Exercise;
  /** Source: which input + value triggered this entry (for debugging/labels). */
  trigger: { inputId: string; value: string };
}

export function resolveSelection(sel: Selection): ResolvedExercise[] {
  const out: ResolvedExercise[] = [];
  const seen = new Set<string>(); // dedup if user picks same exercise twice

  for (const inputId of PRINT_ORDER) {
    // Selects: one value.
    if (sel.selects[inputId]) {
      const value = sel.selects[inputId];
      const exIdx = lib.triggerIndex[`${inputId}|${value}`];
      if (exIdx !== undefined) {
        const ex = lib.exercises[exIdx];
        const dedupKey = `${ex.title}\n${ex.body.join("\n")}`;
        if (!seen.has(dedupKey)) {
          seen.add(dedupKey);
          out.push({ exercise: ex, trigger: { inputId, value } });
        }
      }
    }
    // Checkboxes: zero or more values.
    const checks = sel.checkboxes[inputId];
    if (checks) {
      for (const value of checks) {
        const exIdx = lib.triggerIndex[`${inputId}|${value}`];
        if (exIdx !== undefined) {
          const ex = lib.exercises[exIdx];
          const dedupKey = `${ex.title}\n${ex.body.join("\n")}`;
          if (!seen.has(dedupKey)) {
            seen.add(dedupKey);
            out.push({ exercise: ex, trigger: { inputId, value } });
          }
        }
      }
    }
  }

  return out;
}

// Friendly date format for the printed header. Selection stores mm/dd/yyyy
// (matches the original form). Re-format defensively if a different shape
// shows up (e.g. browser date picker output).
export function formatClassDate(s: string): string {
  if (!s) return "";
  // mm/dd/yyyy already → leave as is.
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)) return s;
  // yyyy-mm-dd → mm/dd/yyyy.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) return `${m[2]}/${m[3]}/${m[1]}`;
  return s;
}
